// opervex-frontend/src/pages/Accounting/CostCenters.js

import React, { useEffect, useState } from "react";
import { Button, Card, Col, Container, Form, InputGroup, Row } from "react-bootstrap";
import { FaPencilAlt, FaPlus, FaSave, FaSearch } from "react-icons/fa";
import { MdOutlineCancel } from "react-icons/md";
import { useParams } from "react-router-dom";
import { SearchHelpModal } from "../../utils/SearchHelp";
import ConfirmService from "../../components/ConfirmationModal/ConfirmService";
import MaskedFormControl from "react-bootstrap-maskedinput";

import useCommandActions, { DEFAULT_ACTIONS } from "../../hooks/useCommandActions";
import { useAuth } from "../../contexts/AuthContext";
import useOpervex from "../../hooks/useOpervex";

const rolePageRequire = "COST_CENTER";

const newDocument = () => ({
  id: "",
  code: "",
  level: 0,
  codeLevel: "",
  highLevel: "",
  description: "",
  disabled: false,
});

const CostCenters = () => {
  const { loadingGlobal } = useAuth();
  const { actions, commandAction } = useCommandActions(rolePageRequire);

  const { id } = useParams();
  const api = useOpervex();

  const [isCreate, setIsCreate] = useState(false);
  const [editing, setEditing] = useState(false);
  const [data, setData] = useState(newDocument());

  // Load
  const load = async (recordId) => {
    setEditing(false);
    setIsCreate(false);

    if (!recordId) return;

    const result = await api.Accounting.CostCenters.findId(recordId);
    if (result?.errors) return await ConfirmService.show("Aviso", result.errors[0]?.message || "Cliente não encontrado", true);

    if (result?.id) commandAction({ ...DEFAULT_ACTIONS, create: true, change: true });
    else commandAction({ ...DEFAULT_ACTIONS, create: true });

    setData(result);
  };

  useEffect(() => {
    if (id) load(id);
    else commandAction({ ...DEFAULT_ACTIONS, create: true });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loadingGlobal) return <div>Carregando...</div>;

  const change = (event) => {
    const { name, value, type, checked } = event.target;

    setData((current) => {
      const updated = {
        ...current,
        [name]: type === "checkbox" ? checked : value,
      };

      const highLevel = updated.highLevel || "";
      const codeLevel = updated.codeLevel || "";
      const code = highLevel ? `${highLevel}.${codeLevel}` : `${codeLevel}`;

      updated.id = code.replace(/\./g, "");
      updated.code = code;

      return updated;
    });
  };

  // Handle Search
  const handleSearch = async () => {
    const item = await SearchHelpModal({
      urlData: "/Accounting/CostCenters/SearchHelp",
      schema: await api.Accounting.CostCenters.schema(),
    });
    if (item?.id) load(item.id);
  };

  // Handle Save
  const handleSave = async () => {
    const result = isCreate
      ? await api.Accounting.CostCenters.create(data)
      : await api.Accounting.CostCenters.update(data.id, data);

    if (result?.errors) return ConfirmService.show("Aviso", result.errors, true);

    await load(data.id || result.id);

    await ConfirmService.show("Aviso", "Registro salvo com sucesso!", true);
  };

  // Handle Create
  const handleCreate = async () => {
    setData({ ...newDocument(), level: 1 });
    setEditing(true);
    setIsCreate(true);

    commandAction({ ...DEFAULT_ACTIONS, save: true });
  };

  // Handle Change
  const handleChange = async () => {
    setEditing(true);
    setIsCreate(false);
    commandAction({ ...DEFAULT_ACTIONS, save: true });
  };

  // Handle Cancel
  const handleCancel = async () => {
    if (isCreate) {
      setData(newDocument());
      setEditing(false);
      commandAction({ ...DEFAULT_ACTIONS, create: true });
    } else {
      load(data.id);
    }
  };

  const masks = {
    2: "11",
    3: "11.11",
    4: "11.11.11",
  };

  const config = {
    mask: masks[data.level] || "11",
  };

  return (
    <Container className="mt-2">
      <Card>
        <Card.Header>
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-0">Centros de Custo</h5>
            </Col>
            <Col className="text-end">
              <Button variant="success" size="sm" className="me-2" onClick={handleCreate} hidden={!actions.create}>
                <FaPlus /> Novo
              </Button>
              <Button variant="primary" size="sm" className="me-2" onClick={handleChange} hidden={!actions.change}>
                <FaPencilAlt /> Alterar
              </Button>
              <Button variant="danger" size="sm" className="me-2" onClick={handleCancel} hidden={!actions.save}>
                <MdOutlineCancel /> Cancelar
              </Button>
              <Button variant="primary" size="sm" className="me-2" onClick={handleSave} hidden={!actions.save}>
                <FaSave /> Salvar
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <Row className="g-2 mb-2">
            <Col md={2}>
              <Form.Label>Código</Form.Label>
              <InputGroup>
                <Form.Control size="sm" value={data.id} disabled />
                <InputGroup.Text role="button" onClick={handleSearch}>
                  <FaSearch />
                </InputGroup.Text>
              </InputGroup>
            </Col>
            <Col md={2}>
              <Form.Label>Formatado</Form.Label>
              <Form.Control size="sm" name="code" value={data.code} onChange={change} disabled />
            </Col>
            <Col md={6}>
              <Form.Label>Descrição</Form.Label>
              <Form.Control size="sm" name="description" value={data.description} onChange={change} disabled={!editing} />
            </Col>
            <Col md={2} className="d-flex align-items-end pb-1">
              <Form.Check label="Inativo" name="disabled" checked={data.disabled} onChange={change} disabled={!editing} />
            </Col>
          </Row>
          <Row className="g-2 mb-2">
            <Col md={2}>
              <Form.Group>
                <Form.Label>Nível</Form.Label>
                <Form.Select size="sm" value={data.level} onChange={change} name="level" disabled={!editing}>
                  <option value={0}>Selecione...</option>
                  <option key={1} value={1}>
                    Nível 1
                  </option>
                  <option key={2} value={2}>
                    Nível 2
                  </option>
                  <option key={3} value={3}>
                    Nível 3
                  </option>
                  <option key={4} value={4}>
                    Nível 4
                  </option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={1}>
              <Form.Label>Codigo</Form.Label>
              <MaskedFormControl
                type="text"
                size="sm"
                mask="11"
                name="codeLevel"
                value={data.codeLevel}
                onChange={change}
                disabled={!editing}
              />
            </Col>
            <Col md={2} hidden={Number(data.level) <= 1}>
              <Form.Label>Nível Superior</Form.Label>
              <MaskedFormControl
                type="text"
                size="sm"
                mask={config.mask}
                name="highLevel"
                value={data.highLevel}
                onChange={change}
                disabled={!editing}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CostCenters;
